import * as React from 'react';
import { useRouteError } from 'react-router-dom';
import { ErrorPage } from '../ErrorPage/ErrorPage';
import { SettingsUnavailableError } from '../../hooks/useSettings';

/**
 * The card a seller gets when a route throws.
 *
 * Split from the boundary below so it can be rendered — and photographed — in
 * every state without a router in an error state, which cannot be built inside
 * a story: react-router throws an invariant on a `<Router>` nested in another.
 */
export const RouteErrorCard: React.FC<{ error?: unknown }> = ({ error }) => {
  // A visitor with no session at all is the one case worth naming. It is a
  // routing fault rather than a fault in the page, the seller has an action
  // that certainly fixes it, and "something went wrong" would send them to
  // support for a sign-in.
  const signedOut = error instanceof SettingsUnavailableError && error.reason === 'signed-out';

  return signedOut ? (
    <ErrorPage
      title="You're signed out"
      message="Your session has ended. Sign in again to pick up where you left off."
      link={{ label: 'Go to sign in', url: '/login' }}
    />
  ) : (
    <ErrorPage
      title="Something went wrong"
      message="This page could not be loaded. Signing in again is usually enough to clear it."
      link={{ label: 'Go to sign in', url: '/login' }}
    />
  );
};

/**
 * The `errorElement` every route in every partner-web router points at.
 *
 * Without one, react-router falls back to its own DefaultErrorComponent, which
 * renders the message and `error.stack` inside a `<pre>` — and does so in a
 * production build too, since only the "Hey developer" note underneath is
 * stripped by the NODE_ENV check. MPPRO-154 is that page: a seller who opened
 * /complete-registration with no session got ten frames of minified stack and
 * nothing to click, which for a self-serve signup is the end of the funnel.
 *
 * Every error shape other than a signed-out session collapses onto one card on
 * purpose. Which internal error fired is not a seller's problem to read, and
 * the way out is the same either way.
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();

  // The frames still have to reach somebody — they go to the console, where a
  // support session or a replay can read them, instead of onto the screen.
  React.useEffect(() => {
    console.error('Route error boundary caught:', error);
  }, [error]);

  return <RouteErrorCard error={error} />;
};
